import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Wand2, Layers, LayoutGrid, SlidersHorizontal, MessageSquare, Download, FileText,
  Play, Maximize2, X, AlertTriangle, Sparkles, Save, RotateCcw, Zap,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErr, fileUrl, runJob } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/AuthContext";
import GenCard, { useGenerationPoll } from "../components/GenCard";

const TABS = [
  ["director", Wand2, "AI Director"],
  ["shots", Layers, "Shots"],
  ["storyboard", LayoutGrid, "Storyboard"],
  ["script", FileText, "Script"],
  ["timeline", SlidersHorizontal, "Timeline"],
  ["review", MessageSquare, "Review"],
  ["export", Download, "Export"],
];

const sel = "rounded-lg border border-[#212230] bg-[#101118] px-3 py-2.5 text-[12.5px] outline-none focus:border-[#ff4b2b]/60";

export default function ProjectDetail() {
  const { id } = useParams();
  const { locale } = useI18n();
  const { refresh } = useAuth();
  const [p, setP] = useState(null);
  const [tab, setTab] = useState("director");
  const [concept, setConcept] = useState("");
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState("");
  const [ids, setIds] = useState([]);
  const [cinema, setCinema] = useState(null);
  const [sb, setSb] = useState(null);
  const [sbBusy, setSbBusy] = useState(false);
  const [filling, setFilling] = useState("");
  const [continuity, setContinuity] = useState(null);
  const [improve, setImprove] = useState(null);
  const [board, setBoard] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [exports, setExports] = useState([]);
  const [comment, setComment] = useState({ timecode: 0, text: "" });
  const [aiEdit, setAiEdit] = useState("");

  const load = useCallback(() => {
    api.get(`/projects/${id}`).then(({ data }) => {
      setP(data);
      setTimeline(data.timeline);
      if (data.production_bible || data.is_inbox) setTab((t) => (t === "director" ? "shots" : t));
    }).catch(() => {});
  }, [id]);

  const loadStoryboard = useCallback(() => {
    api.get(`/projects/${id}/storyboard/estimate`).then(({ data }) => setSb(data)).catch(() => {});
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStoryboard(); }, [loadStoryboard]);
  useEffect(() => { api.get("/exports").then(({ data }) => setExports(data.filter((e) => e.project_id === id))).catch(() => {}); }, [id]);

  const onDone = useCallback(() => { refresh(); load(); }, [refresh, load]);
  const gens = useGenerationPoll(ids, onDone);

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const direct = () => run("direct", async () => {
    const data = await runJob("/ai/director", {
      concept: concept || p.description || p.title,
      target_duration: p.target_duration, genre: p.genre,
      visual_style: p.visual_style, aspect_ratio: p.aspect_ratio, language: locale,
      project_id: id,
    });
    setPlan(data);
  });

  const applyPlan = () => run("apply", async () => {
    const { data } = await api.post("/ai/director/apply", { project_id: id, plan });
    toast.success(`${data.sequences} sequences · ${data.shots} shots`);
    setPlan(null);
    load();
    setTab("shots");
  });

  const genShot = (shot) => run(`shot-${shot.id}`, async () => {
    const { data } = await api.post("/generations", {
      kind: "video",
      prompt: shot.prompt || `${shot.title}. ${shot.action}`,
      title: shot.title, duration: shot.duration, quality: shot.quality || "standard",
      resolution: p.resolution === "4k" ? "1080p" : p.resolution,
      aspect_ratio: p.aspect_ratio, routing: "auto",
      project_id: id, shot_id: shot.id, sequence_id: shot.sequence_id,
      character_ids: shot.character_ids || [], location_id: shot.location_id || null,
    });
    setIds((s) => [data.id, ...s]);
    await refresh();
    load();
  });

  const genSequence = (seqId) => {
    const shots = p.shots.filter((s) => s.sequence_id === seqId);
    shots.forEach((s) => genShot(s));
  };

  const storyboardAll = async () => {
    setSbBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/storyboard`);
      toast.success(locale === "fr"
        ? `${data.queued} images de storyboard lancées (${data.credits} crédits)`
        : `${data.queued} storyboard frames queued (${data.credits} credits)`);
      await refresh();
      loadStoryboard();
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSbBusy(false);
    }
  };

  const fillShots = async (seq) => {    setFilling(seq.id);
    try {
      const { data } = await api.post("/ai/sequences/fill-shots", {
        sequence_id: seq.id, count: Math.min(seq.shots_planned || 8, 20),
      });
      toast.success(locale === "fr" ? `${data.created} plans écrits` : `${data.created} shots written`);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setFilling("");
    }
  };

  const genStoryboard = () => run("board", async () => {
    const data = await runJob("/ai/storyboard", {
      project_id: id, concept: p.logline || p.description || p.title,
      scene_count: Math.max(4, Math.min(Math.round(p.target_duration / 10), 12)),
      aspect_ratio: p.aspect_ratio, language: locale,
    });
    setBoard(data);
  });

  const boardToShot = (scene) => run(`bs-${scene.order}`, async () => {
    const { data } = await api.post("/generations", {
      kind: "image", prompt: scene.prompt, title: scene.title, quality: "preview",
      aspect_ratio: p.aspect_ratio, project_id: id,
    });
    setIds((s) => [data.id, ...s]);
    await refresh();
  });

  const nextScene = (mode) => run(`next-${mode}`, async () => {
    const data = await runJob("/ai/next-scene", { project_id: id, mode });
    toast.success(`${data.shots} shots added`);
    load();
  });

  const checkContinuity = () => run("continuity", async () => {
    const data = await runJob("/ai/continuity", { project_id: id });
    setContinuity(data);
  });

  const improveFilm = () => run("improve", async () => {
    const data = await runJob("/ai/improve", { project_id: id, focus: "all" });
    setImprove(data);
  });

  const genScript = () => run("script", async () => {
    const data = await runJob("/ai/script", {
      project_id: id, concept: p.logline || p.description || p.title, language: locale,
    });
    setP((s) => ({ ...s, script: data.screenplay }));
  });

  const autobuild = () => run("tl", async () => {
    const { data } = await api.post(`/projects/${id}/timeline/autobuild`);
    setTimeline(data);
    toast.success(locale === "fr" ? "Timeline assemblée" : "Timeline assembled");
  });

  const saveTimeline = () => run("tlsave", async () => {
    await api.put(`/projects/${id}/timeline`, timeline);
    toast.success("Saved");
  });

  const runAiEdit = () => run("aiedit", async () => {
    const { data } = await api.post("/ai/timeline-edit", { project_id: id, instruction: aiEdit });
    toast.success(data.reply || "Done");
  });

  const doExport = (format, resolution, ratio) => run("export", async () => {
    const { data } = await api.post(`/projects/${id}/exports`, { format, resolution, aspect_ratio: ratio, subtitles: true });
    setExports((s) => [data, ...s]);
    toast.success(data.status === "empty" ? (locale === "fr" ? "Aucun plan généré" : "No generated shots yet") : "Export ready");
  });

  const addComment = () => run("cmt", async () => {
    await api.post(`/projects/${id}/comments`, comment);
    setComment({ timecode: 0, text: "" });
    load();
  });

  const snapshot = () => run("snap", async () => {
    await api.post(`/projects/${id}/versions`, { label: `Snapshot ${new Date().toLocaleString()}` });
    load();
    toast.success(locale === "fr" ? "Version enregistrée" : "Version saved");
  });

  if (!p) return <div className="p-10 text-[13px] text-[#9194a6]">…</div>;

  const completed = p.shots.filter((s) => s.asset_id);

  return (
    <div data-testid="project-detail">
      <div className="sticky top-14 z-20 border-b border-[#212230] bg-[#08080d]/95 px-6 py-4 backdrop-blur-xl lg:px-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="mono text-[10px] uppercase tracking-[0.16em] text-[#8288a0]" data-testid="project-film-meta">
              {p.type} · {locale === "fr" ? "cible" : "target"}{" "}
              {p.target_duration >= 60 ? `${Math.round(p.target_duration / 60)} min` : `${p.target_duration}s`}
              {" · "}{p.shots.length} {locale === "fr" ? "plans" : "shots"}
              {" · "}{locale === "fr" ? "monté" : "cut"} {Math.round(p.shots.reduce((a, s) => a + Number(s.duration || 0), 0))}s
              {" · "}{p.aspect_ratio} · {p.visual_style}
            </p>
            {sb?.missing > 0 && (
              <div className="mt-4">
                <button onClick={storyboardAll} disabled={sbBusy} data-testid="storyboard-all-btn" className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11.5px] font-semibold disabled:opacity-50">
                  <LayoutGrid className="h-3 w-3" />
                  {sbBusy
                    ? (locale === "fr" ? "Lancement…" : "Starting…")
                    : (locale === "fr" ? `Storyboard du film · ${sb.total_credits} cr`
                                       : `Storyboard the film · ${sb.total_credits} cr`)}
                </button>
                <p className="mono mt-2.5 max-w-[620px] text-[10.5px] leading-relaxed text-[#8288a0]" data-testid="storyboard-hint">
                  {locale === "fr"
                    ? `${sb.missing} plan(s) sans image · ${sb.credits_per_frame} crédits l'image contre 70 pour un plan vidéo standard : validez le film en images avant de dépenser en vidéo.`
                    : `${sb.missing} shot(s) without a frame · ${sb.credits_per_frame} credits per image against 70 for a standard video shot: approve the film in frames before spending on video.`}
                </p>
              </div>
            )}
            <h1 className="mt-1 font-display text-xl font-semibold sm:text-2xl">{p.title}</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={snapshot} data-testid="snapshot-btn" className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11.5px] font-semibold">
              <Save className="h-3 w-3" /> {locale === "fr" ? "Version" : "Snapshot"}
            </button>
            {completed.length > 0 && (
              <button onClick={() => setCinema(completed[0])} data-testid="cinema-mode-btn" className="btn-amber inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11.5px] font-semibold">
                <Maximize2 className="h-3 w-3" /> Cinema Mode
              </button>
            )}
          </div>
        </div>

        <div className="mt-4 flex gap-1.5 overflow-x-auto">
          {TABS.map(([k, Icon, label]) => (
            <button
              key={k}
              onClick={() => { setTab(k); if (k === "storyboard" && !board) genStoryboard(); }}
              data-testid={`tab-${k}`}
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-[11.5px] font-semibold transition-colors duration-200 ${
                tab === k ? "bg-white text-black" : "border border-[#2a2c3c] text-[#9194a6] hover:text-white"
              }`}
            >
              <Icon className="h-3 w-3" /> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6 lg:p-10">
        {/* DIRECTOR */}
        {tab === "director" && (
          <div className="max-w-[1000px]" data-testid="tab-panel-director">
            <p className="overline">AI Director</p>
            <textarea
              rows={4}
              value={concept}
              onChange={(e) => setConcept(e.target.value)}
              data-testid="director-concept"
              placeholder={locale === "fr"
                ? "Je veux un court-métrage de 5 minutes sur une femme qui découvre une ville abandonnée…"
                : "I want a five-minute short film about a woman discovering an abandoned city…"}
              className="mt-4 w-full resize-none rounded-2xl border border-[#212230] bg-[#0b0b11] p-5 text-[15px] leading-relaxed outline-none placeholder:text-[#8288a0] focus:border-[#ff4b2b]/50"
            />
            <button onClick={direct} disabled={busy === "direct"} data-testid="director-generate" className="btn-amber mt-4 inline-flex items-center gap-2 rounded-full px-6 py-3 text-[13px] font-semibold disabled:opacity-50">
              <Wand2 className="h-4 w-4" />
              {busy === "direct" ? (locale === "fr" ? "Mise en scène…" : "Directing…") : locale === "fr" ? "Générer le plan de production" : "Generate production plan"}
            </button>

            {plan && (
              <div className="mt-8 rounded-2xl border border-[#ff4b2b]/30 bg-[#ff4b2b]/[0.04] p-6" data-testid="director-plan">
                <h2 className="font-display text-2xl font-semibold">{plan.title}</h2>
                <p className="mt-3 text-[14px] italic text-[#e0c2a8]">{plan.logline}</p>
                <p className="mt-4 text-[13.5px] leading-relaxed text-[#c0c3d0]">{plan.synopsis}</p>
                <div className="mt-6 grid gap-4 sm:grid-cols-3">
                  {[["Characters", plan.characters], ["Locations", plan.locations], ["Acts", plan.acts]].map(([label, arr]) => (
                    <div key={label} className="rounded-xl border border-[#212230] bg-[#0b0b11] p-4">
                      <p className="overline">{label}</p>
                      <ul className="mt-2 space-y-1">
                        {(arr || []).map((x, i) => (
                          <li key={i} className="text-[12.5px] text-[#c0c3d0]">{x.name || x.title}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <div className="mt-6 space-y-3">
                  {(plan.sequences || []).map((s, i) => (
                    <div key={i} className="rounded-xl border border-[#212230] bg-[#0b0b11] p-4">
                      <p className="mono text-[10.5px] uppercase tracking-[0.14em] text-[#ff4b2b]">
                        SEQ {String(s.order || i + 1).padStart(2, "0")} · ACT {s.act} · {s.duration}s · {(s.shots || []).length} shots
                      </p>
                      <p className="mt-1.5 text-[13.5px] font-medium">{s.title}</p>
                      <p className="mt-1 text-[12px] text-[#9194a6]">{s.description}</p>
                    </div>
                  ))}
                </div>
                <button onClick={applyPlan} disabled={busy === "apply"} data-testid="director-apply" className="btn-primary mt-6 rounded-full px-6 py-3 text-[13px] font-semibold disabled:opacity-50">
                  {busy === "apply" ? "…" : locale === "fr" ? "Appliquer au projet" : "Apply to project"}
                </button>
              </div>
            )}

            {p.production_bible && (
              <div className="mt-10" data-testid="production-bible">
                <p className="overline">Production Bible</p>
                <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {Object.entries(p.production_bible).map(([k, v]) => (
                    <div key={k} className="rounded-xl border border-[#212230] bg-[#0b0b11] p-5">
                      <p className="mono text-[10px] uppercase tracking-[0.16em] text-[#ff4b2b]">{k.replace("_", " ")}</p>
                      <div className="mt-2 space-y-1 text-[12.5px] leading-relaxed text-[#c0c3d0]">
                        {Array.isArray(v)
                          ? v.map((x, i) => <p key={i}>{typeof x === "string" ? x : x.name || x.title || JSON.stringify(x).slice(0, 90)}</p>)
                          : typeof v === "object" && v
                          ? Object.entries(v).map(([kk, vv]) => <p key={kk}><span className="text-[#8288a0]">{kk}: </span>{String(vv)}</p>)
                          : <p>{String(v)}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* SHOTS */}
        {tab === "shots" && (
          <div data-testid="tab-panel-shots">
            <div className="flex flex-wrap gap-2">
              {[["continue", locale === "fr" ? "Scène suivante" : "Next scene"],
                ["alternate", locale === "fr" ? "Scène alternative" : "Alternate scene"],
                ["ending", locale === "fr" ? "Fin alternative" : "Alternate ending"],
                ["extend", locale === "fr" ? "Prolonger" : "Extend"]].map(([m, label]) => (
                <button key={m} onClick={() => nextScene(m)} disabled={busy === `next-${m}`} data-testid={`next-scene-${m}`} className="btn-ghost rounded-full px-4 py-2 text-[11.5px] font-semibold disabled:opacity-50">
                  {busy === `next-${m}` ? "…" : label}
                </button>
              ))}
              <button onClick={checkContinuity} disabled={busy === "continuity"} data-testid="continuity-btn" className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11.5px] font-semibold disabled:opacity-50">
                <AlertTriangle className="h-3 w-3" /> {locale === "fr" ? "Vérifier la continuité" : "Check continuity"}
              </button>
              <button onClick={improveFilm} disabled={busy === "improve"} data-testid="improve-btn" className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11.5px] font-semibold disabled:opacity-50">
                <Sparkles className="h-3 w-3" /> {locale === "fr" ? "Améliorer le film" : "Improve film"}
              </button>
            </div>

            {continuity && (
              <div className="mt-6 rounded-2xl border border-[#212230] bg-[#0b0b11] p-6" data-testid="continuity-result">
                <p className="overline">Continuity · score {continuity.score}</p>
                <div className="mt-4 space-y-3">
                  {(continuity.warnings || []).map((w, i) => (
                    <div key={i} className="rounded-lg border border-[#ff4b2b]/25 bg-[#ff4b2b]/[0.05] p-4">
                      <p className="mono text-[10px] uppercase tracking-[0.14em] text-[#ff4b2b]">
                        {w.severity} · {w.category} · shots {(w.shots || []).join(", ")}
                      </p>
                      <p className="mt-1.5 text-[13px]">{w.message}</p>
                      <p className="mt-1 text-[12px] text-[#9194a6]">→ {w.fix}</p>
                    </div>
                  ))}
                  {(continuity.warnings || []).length === 0 && (
                    <p className="text-[13px] text-[#00ff9d]">{locale === "fr" ? "Aucune incohérence détectée." : "No continuity issues found."}</p>
                  )}
                </div>
              </div>
            )}

            {improve && (
              <div className="mt-6 rounded-2xl border border-[#212230] bg-[#0b0b11] p-6" data-testid="improve-result">
                <p className="overline">AI Director's Cut</p>
                <p className="mt-3 text-[13.5px] leading-relaxed text-[#c0c3d0]">{improve.summary}</p>
                <p className="mono mt-3 text-[11px] text-[#ff4b2b]">
                  {locale === "fr" ? "Durée recommandée" : "Recommended duration"}: {improve.recommended_duration}s
                </p>
                <div className="mt-4 space-y-2">
                  {(improve.suggestions || []).map((s, i) => (
                    <div key={i} className="rounded-lg border border-[#1a1b26] bg-[#0e0e15] p-4">
                      <p className="mono text-[10px] uppercase tracking-[0.14em] text-[#00d5ff]">{s.area} · {s.impact}</p>
                      <p className="mt-1.5 text-[13px]">{s.action}</p>
                      <p className="mt-1 text-[12px] text-[#9194a6]">{s.reason}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {ids.length > 0 && (
              <div className="mt-8">
                <p className="overline">{locale === "fr" ? "En cours" : "In flight"}</p>
                <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {ids.slice(0, 8).map((gid) => <GenCard key={gid} gen={gens[gid]} compact />)}
                </div>
              </div>
            )}

            <div className="mt-10 space-y-8">
              {p.sequences.length === 0 && (
                <p className="text-[13px] text-[#9194a6]">
                  {locale === "fr" ? "Utilisez l'AI Director pour construire les séquences." : "Use the AI Director to build sequences."}
                </p>
              )}
              {p.sequences.map((seq) => {
                const shots = p.shots.filter((s) => s.sequence_id === seq.id);
                return (
                  <div key={seq.id} data-testid={`sequence-${seq.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="mono text-[10.5px] uppercase tracking-[0.14em] text-[#ff4b2b]">
                          ACT {seq.act} · SEQ {String(seq.order).padStart(2, "0")} · {seq.target_duration}s
                        </p>
                        <p className="mt-1 font-display text-lg font-medium">{seq.title}</p>
                      </div>
                      <div className="flex gap-2">
                        {shots.length === 0 && (
                          <button onClick={() => fillShots(seq)} disabled={filling === seq.id} data-testid={`fill-shots-${seq.id}`} className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11.5px] font-semibold disabled:opacity-50">
                            {filling === seq.id
                              ? (locale === "fr" ? "Écriture…" : "Writing…")
                              : (locale === "fr" ? `Écrire les ${seq.shots_planned || 8} plans` : `Write the ${seq.shots_planned || 8} shots`)}
                          </button>
                        )}
                        <button onClick={() => genSequence(seq.id)} data-testid={`gen-sequence-${seq.id}`} className="btn-amber inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11.5px] font-semibold">
                          <Zap className="h-3 w-3" /> {locale === "fr" ? "Générer la séquence" : "Generate sequence"}
                        </button>
                      </div>
                    </div>
                    <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                      {shots.map((s) => (
                        <div key={s.id} className="overflow-hidden rounded-xl border border-[#212230] bg-[#0b0b11] transition-colors duration-200 hover:border-[#2f3142]" data-testid={`shot-${s.id}`}>
                          <div className="relative aspect-video bg-[#0e0e15]">
                            {s.asset_id ? (
                              <>
                                <video src={fileUrl(s.asset_id)} preload="metadata" playsInline className="h-full w-full object-cover" muted onMouseEnter={(e) => e.currentTarget.play().catch(() => {})} onMouseLeave={(e) => e.currentTarget.pause()} />
                                <button
                                  onClick={() => setCinema(s)}
                                  data-testid={`shot-open-${s.id}`}
                                  aria-label={locale === "fr" ? "Ouvrir le plan" : "Open shot"}
                                  className="group absolute inset-0 grid place-items-center bg-black/0 transition-colors duration-200 hover:bg-black/35"
                                >
                                  <span className="grid h-11 w-11 place-items-center rounded-full bg-black/60 opacity-80 backdrop-blur transition-transform duration-200 group-hover:scale-110 group-hover:opacity-100">
                                    <Play className="h-4 w-4" />
                                  </span>
                                </button>
                              </>
                            ) : (
                              <div className="grid h-full place-items-center">
                                <p className="mono text-[10px] uppercase tracking-[0.2em] text-[#8288a0]">
                                  {s.status === "generating" ? (locale === "fr" ? "Génération…" : "Generating…") : "EMPTY FRAME"}
                                </p>
                              </div>
                            )}
                          </div>
                          <div className="p-4">
                            <p className="mono text-[10px] uppercase tracking-[0.14em] text-[#8288a0]">
                              SHOT {String(s.order).padStart(2, "0")} · {s.duration}s · {s.shot_type}
                            </p>
                            <p className="mt-1.5 text-[13px] font-medium">{s.title}</p>
                            <p className="mono mt-1 text-[10px] text-[#8288a0]">
                              {s.camera} · {s.lens} · {s.lighting}
                            </p>
                            {s.versions?.length > 0 && (
                              <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`shot-versions-${s.id}`}>
                                {s.versions.map((v) => (
                                  <button
                                    key={v.version}
                                    onClick={async () => {
                                      await api.post(`/shots/${s.id}/use-version/${v.version}`);
                                      load();
                                    }}
                                    className={`mono rounded px-2 py-0.5 text-[9.5px] ${
                                      s.active_version === v.version ? "bg-[#ff4b2b] text-white" : "border border-[#2a2c3c] text-[#9194a6]"
                                    }`}
                                  >
                                    V{v.version}
                                  </button>
                                ))}
                              </div>
                            )}
                            <div className="mt-3 flex gap-2">
                              {s.asset_id && (
                                <>
                                  <button
                                    onClick={() => setCinema(s)}
                                    data-testid={`shot-play-${s.id}`}
                                    className="btn-amber flex-1 rounded-full py-1.5 text-[11px] font-semibold"
                                  >
                                    {locale === "fr" ? "Ouvrir" : "Open"}
                                  </button>
                                  <a
                                    href={fileUrl(s.asset_id)}
                                    download
                                    data-testid={`shot-download-${s.id}`}
                                    aria-label={locale === "fr" ? "Télécharger le plan" : "Download shot"}
                                    className="btn-ghost grid place-items-center rounded-full px-3 py-1.5"
                                  >
                                    <Download className="h-3 w-3" />
                                  </a>
                                </>
                              )}
                              <button
                                onClick={() => genShot(s)}
                                disabled={busy === `shot-${s.id}`}
                                data-testid={`gen-shot-${s.id}`}
                                className="btn-ghost flex-1 rounded-full py-1.5 text-[11px] font-semibold disabled:opacity-50"
                              >
                                {s.asset_id ? (locale === "fr" ? "Régénérer" : "Regenerate") : locale === "fr" ? "Générer" : "Generate"}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* STORYBOARD */}
        {tab === "storyboard" && (
          <div data-testid="tab-panel-storyboard">
            <div className="flex items-center justify-between">
              <p className="overline">Visual storyboard</p>
              <button onClick={genStoryboard} disabled={busy === "board"} data-testid="storyboard-generate" className="btn-ghost rounded-full px-4 py-2 text-[11.5px] font-semibold disabled:opacity-50">
                {busy === "board" ? "…" : locale === "fr" ? "Régénérer" : "Regenerate"}
              </button>
            </div>
            {!board && <p className="mt-6 text-[13px] text-[#9194a6]">…</p>}
            {board && (
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {(board.scenes || []).map((s) => (
                  <div key={s.order} className="rounded-xl border border-[#212230] bg-[#0b0b11] p-5" data-testid={`board-scene-${s.order}`}>
                    <p className="mono text-[10px] uppercase tracking-[0.14em] text-[#ff4b2b]">
                      SCENE {String(s.order).padStart(2, "0")} · {s.start}–{s.end}s
                    </p>
                    <p className="mt-2 font-display text-base font-medium">{s.title}</p>
                    <p className="mt-2 text-[12.5px] leading-relaxed text-[#9194a6]">{s.description}</p>
                    <p className="mono mt-3 text-[10px] text-[#8288a0]">{s.camera} · {s.lighting} · {s.transition}</p>
                    <button onClick={() => boardToShot(s)} data-testid={`board-generate-${s.order}`} className="btn-ghost mt-4 w-full rounded-full py-2 text-[11px] font-semibold">
                      {locale === "fr" ? "Générer l'image" : "Generate frame"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* SCRIPT */}
        {tab === "script" && (
          <div className="max-w-[860px]" data-testid="tab-panel-script">
            <div className="flex items-center justify-between">
              <p className="overline">Screenplay</p>
              <button onClick={genScript} disabled={busy === "script"} data-testid="script-generate" className="btn-amber rounded-full px-5 py-2 text-[11.5px] font-semibold disabled:opacity-50">
                {busy === "script" ? "…" : locale === "fr" ? "Générer le scénario" : "Generate screenplay"}
              </button>
            </div>
            <pre className="mono mt-6 whitespace-pre-wrap rounded-2xl border border-[#212230] bg-[#0b0b11] p-6 text-[12.5px] leading-relaxed text-[#c0c3d0]">
              {p.script || (locale === "fr" ? "Aucun scénario pour l'instant." : "No screenplay yet.")}
            </pre>
          </div>
        )}

        {/* TIMELINE */}
        {tab === "timeline" && timeline && (
          <div data-testid="tab-panel-timeline">
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={autobuild} disabled={busy === "tl"} data-testid="timeline-autobuild" className="btn-amber rounded-full px-4 py-2 text-[11.5px] font-semibold disabled:opacity-50">
                {locale === "fr" ? "Assembler les plans" : "Assemble shots"}
              </button>
              <button onClick={saveTimeline} data-testid="timeline-save" className="btn-ghost rounded-full px-4 py-2 text-[11.5px] font-semibold">
                {locale === "fr" ? "Enregistrer" : "Save"}
              </button>
              <input
                value={aiEdit}
                onChange={(e) => setAiEdit(e.target.value)}
                data-testid="timeline-ai-input"
                placeholder={locale === "fr" ? "Rends cette séquence plus dramatique…" : "Make this sequence more dramatic…"}
                className={`${sel} min-w-[260px] flex-1`}
              />
              <button onClick={runAiEdit} disabled={busy === "aiedit" || !aiEdit} data-testid="timeline-ai-edit" className="btn-ghost rounded-full px-4 py-2 text-[11.5px] font-semibold disabled:opacity-50">
                AI Edit
              </button>
            </div>

            <div className="mt-8 overflow-x-auto rounded-2xl border border-[#212230] bg-[#0b0b11] p-5">
              <div className="mono mb-3 flex justify-between text-[10px] text-[#8288a0]" style={{ minWidth: 900 }}>
                <span>00:00</span>
                <span>{Math.round((timeline.duration || 60) / 2)}s</span>
                <span>{Math.round(timeline.duration || 60)}s</span>
              </div>
              <div style={{ minWidth: 900 }}>
                {timeline.tracks.map((tr) => (
                  <div key={tr.id} className="flex items-center gap-3 border-t border-[#16181f] py-2.5" data-testid={`track-${tr.kind}`}>
                    <span className="mono w-20 shrink-0 text-[9.5px] uppercase text-[#9194a6]">{tr.name}</span>
                    <div className="track-row relative h-8 flex-1 rounded bg-white/[0.03]">
                      {(tr.clips || []).map((c) => (
                        <div
                          key={c.id}
                          title={c.name}
                          className="absolute top-0 h-8 overflow-hidden rounded border border-white/20 px-2 py-1.5"
                          style={{
                            left: `${(c.start / (timeline.duration || 60)) * 100}%`,
                            width: `${Math.max((c.duration / (timeline.duration || 60)) * 100, 2)}%`,
                            background: tr.kind === "video" ? "#ff4b2b55" : "#00d5ff33",
                          }}
                        >
                          <p className="mono truncate text-[9px]">{c.name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* REVIEW */}
        {tab === "review" && (
          <div className="max-w-[900px]" data-testid="tab-panel-review">
            <p className="overline">Review mode</p>
            <div className="mt-5 flex flex-wrap gap-2">
              <input type="number" value={comment.timecode} onChange={(e) => setComment({ ...comment, timecode: Number(e.target.value) })} className={`${sel} w-24`} placeholder="sec" data-testid="comment-timecode" />
              <input value={comment.text} onChange={(e) => setComment({ ...comment, text: e.target.value })} className={`${sel} flex-1`} placeholder={locale === "fr" ? "Remplace ce plan…" : "Replace this shot…"} data-testid="comment-text" />
              <button onClick={addComment} data-testid="comment-add" className="btn-amber rounded-full px-5 py-2 text-[11.5px] font-semibold">
                {locale === "fr" ? "Commenter" : "Comment"}
              </button>
            </div>
            <div className="mt-6 space-y-2">
              {(p.comments || []).map((c) => (
                <div key={c.id} className="flex items-start gap-4 rounded-lg border border-[#1a1b26] bg-[#0e0e15] p-4" data-testid={`comment-${c.id}`}>
                  <span className="mono shrink-0 text-[11px] text-[#ff4b2b]">
                    {String(Math.floor(c.timecode / 60)).padStart(2, "0")}:{String(c.timecode % 60).padStart(2, "0")}
                  </span>
                  <p className="text-[13px]">{c.text}</p>
                </div>
              ))}
              {(p.comments || []).length === 0 && <p className="text-[13px] text-[#9194a6]">—</p>}
            </div>

            {p.versions?.length > 0 && (
              <div className="mt-10">
                <p className="overline">{locale === "fr" ? "Versions du projet" : "Project versions"}</p>
                <div className="mt-4 space-y-2">
                  {p.versions.map((v) => (
                    <div key={v.id} className="flex items-center justify-between rounded-lg border border-[#1a1b26] bg-[#0e0e15] p-4">
                      <p className="text-[12.5px]">{v.label}</p>
                      <button
                        onClick={async () => { await api.post(`/projects/${id}/versions/${v.id}/restore`); load(); toast.success("Restored"); }}
                        data-testid={`restore-${v.id}`}
                        className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold"
                      >
                        <RotateCcw className="h-3 w-3" /> {locale === "fr" ? "Restaurer" : "Restore"}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* EXPORT */}
        {tab === "export" && (
          <div className="max-w-[900px]" data-testid="tab-panel-export">
            <p className="overline">Export</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {[["mp4", "1080p", "16:9"], ["mp4", "1080p", "9:16"], ["mp4", "1080p", "1:1"],
                ["mp4", "1080p", "4:5"], ["mp4", "4k", "21:9"], ["mov", "1080p", "2.39:1"]].map(([fmt, res, ratio]) => (
                <button key={`${fmt}${res}${ratio}`} onClick={() => doExport(fmt, res, ratio)} data-testid={`export-${ratio}`} className="btn-ghost rounded-full px-4 py-2 text-[11.5px] font-semibold">
                  {fmt.toUpperCase()} · {res} · {ratio}
                </button>
              ))}
            </div>
            <div className="mt-8 space-y-2">
              {exports.map((e) => (
                <div key={e.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#1a1b26] bg-[#0e0e15] p-4" data-testid={`export-row-${e.id}`}>
                  <p className="mono text-[11px] uppercase text-[#9194a6]">
                    {e.format} · {e.resolution} · {e.aspect_ratio} · {e.shot_count} shots · {Math.round(e.duration)}s
                  </p>
                  <span className={`mono text-[10px] uppercase ${e.status === "ready" ? "text-[#00ff9d]" : "text-[#ff8a8a]"}`}>{e.status}</span>
                </div>
              ))}
              {exports.length === 0 && <p className="text-[13px] text-[#9194a6]">—</p>}
            </div>
            <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {completed.map((s) => (
                <a key={s.id} href={fileUrl(s.asset_id)} download className="rounded-xl border border-[#212230] bg-[#0b0b11] p-4 text-[12px] transition-colors hover:border-[#383b4d]">
                  <Download className="mb-2 h-3.5 w-3.5 text-[#ff4b2b]" />
                  {s.title}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* CINEMA MODE */}
      {cinema && (
        <div className="fixed inset-0 z-[60] bg-black" data-testid="cinema-mode">
          <button onClick={() => setCinema(null)} data-testid="cinema-exit" className="absolute right-6 top-6 z-10 grid h-10 w-10 place-items-center rounded-full border border-white/20 bg-black/60 backdrop-blur">
            <X className="h-4 w-4" />
          </button>
          <div className="grid h-full place-items-center px-6">
            <video src={fileUrl(cinema.asset_id)} controls autoPlay className="max-h-[80vh] w-full max-w-[1400px]" />
          </div>
          <div className="absolute inset-x-0 bottom-8 flex justify-center gap-2 px-6">
            {completed.map((s) => (
              <button
                key={s.id}
                onClick={() => setCinema(s)}
                className={`h-1 rounded-full transition-all duration-300 ${cinema.id === s.id ? "w-10 bg-[#ff4b2b]" : "w-4 bg-white/25"}`}
              />
            ))}
          </div>
          <p className="mono absolute bottom-2 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.24em] text-[#8288a0]">
            ESC — exit cinema mode
          </p>
        </div>
      )}
    </div>
  );
}
